import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Check, Loader2, Zap, MapPin, ClipboardList, Camera, FileSignature,
  Mail, Wallet, Ruler, Truck, CalendarClock, HardHat, Sparkles, Handshake,
  Image as ImageIcon, X, ChevronLeft, ChevronRight, Star, Send,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Iter89j (2026-02-21) — Stage-key → Icon-Mapping. Fallbacks: Sparkles.
const STAGE_ICONS = {
  lead: ClipboardList,
  tech_check: Camera,
  orientation_offer_sent: Mail,
  offer_created: FileSignature,
  offer_signed: FileSignature,
  order_confirmed: Mail,
  down_payment_received: Wallet,
  heat_load_appointment: Ruler,
  installer_handover: Truck,
  installation_scheduled: CalendarClock,
  installation_start: HardHat,
  commissioning: Sparkles,
  installation_done: Sparkles,
  handover: Handshake,
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch {
    return iso;
  }
};

/**
 * Iter89j — Öffentliche, kunden-facing Tracking-Seite.
 * URL: /track/:token
 *
 * Enhancements:
 *  - Visuelle Zeitachse mit vertikaler Verbindungslinie
 *  - Stage-spezifische Icons (lucide)
 *  - Foto-Galerie pro Meilenstein (kleine Thumbs, Klick → Lightbox)
 */
export default function PublicTrackingPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // { stageIdx, photoIdx }
  const [feedback, setFeedback] = useState(null); // {eligible, already_submitted, existing}

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/public/track/${encodeURIComponent(token)}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Tracking-Link unbekannt"))
      .finally(() => setLoading(false));
    // Iter89r — Foto-/Sterne-Feedback nur ab Commissioning
    axios
      .get(`${API}/public/track/${encodeURIComponent(token)}/feedback-status`)
      .then((r) => setFeedback(r.data))
      .catch(() => setFeedback(null));
  }, [token]);

  const photoUrl = (photoId) =>
    `${API}/public/track/${encodeURIComponent(token)}/photo/${encodeURIComponent(photoId)}`;

  const totalPhotos = useMemo(() => {
    if (!data?.stages) return 0;
    return data.stages.reduce((n, s) => n + (s.photos?.length || 0), 0);
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] text-zinc-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-[#00E575]" size={32} />
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="min-h-screen bg-[#09090B] text-zinc-50 flex flex-col items-center justify-center px-6 text-center">
        <div className="text-[60px] mb-4">🔍</div>
        <div className="font-display text-xl font-bold mb-2">Link unbekannt</div>
        <div className="text-zinc-400">{err || "Dieser Tracking-Link ist nicht (mehr) gültig."}</div>
      </div>
    );
  }

  const completedCount = data.stages.filter((s) => s.completed).length;
  const progressPct = Math.round((completedCount / data.stages.length) * 100);

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#00E575]/20 via-[#0E0E12] to-[#09090B] border-b border-zinc-800">
        <div className="max-w-2xl mx-auto px-5 py-8">
          <div className="inline-flex items-center gap-1.5 bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/40 rounded-full px-3 py-1 text-[12px] font-mono uppercase tracking-wider font-bold mb-3">
            <Zap size={11} /> NOVATUS Energie
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold leading-tight" data-testid="tracking-title">
            Ihr Wärmepumpen-Projekt
          </h1>
          <p className="text-zinc-400 mt-1.5">
            {data.customer_first_name && <>Hallo {data.customer_first_name}, </>}
            so steht es um Ihren Auftrag {data.customer_city && <>in {data.customer_city}</>}.
          </p>

          {/* Progress bar */}
          <div className="mt-5">
            <div className="flex justify-between items-baseline mb-1.5">
              <div className="text-[12px] text-zinc-400 font-mono uppercase tracking-wider">Fortschritt</div>
              <div className="text-[12px] text-[#00E575] font-mono font-bold" data-testid="tracking-progress-pct">{progressPct}%</div>
            </div>
            <div className="h-2.5 bg-zinc-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#00E575] to-[#00C964] rounded-full transition-all duration-700"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <div className="text-[12px] text-zinc-300 mt-2 font-medium">
              Referenz {data.project_short_id} · zuletzt aktualisiert {fmtDate(data.updated_at)}
              {totalPhotos > 0 && (
                <span className="text-zinc-500"> · {totalPhotos} Foto{totalPhotos !== 1 ? "s" : ""} dokumentiert</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="max-w-2xl mx-auto px-5 py-8">
        <ol className="relative space-y-6" data-testid="tracking-timeline">
          {/* vertikale Zeitachse (absolute, hinter den Icons) */}
          <div
            aria-hidden="true"
            className="absolute left-[19px] top-2 bottom-2 w-[2px] bg-gradient-to-b from-zinc-800 via-zinc-800 to-zinc-800/40"
          />

          {data.stages.map((s, idx) => {
            const done = s.completed;
            const isCurrent = s.is_current;
            const Icon = STAGE_ICONS[s.key] || Sparkles;
            return (
              <li
                key={s.key}
                data-testid={`stage-${s.key}`}
                className="relative pl-14"
              >
                {/* Icon-Bubble sitzt AUF der Timeline */}
                <div
                  className={`absolute left-0 top-0 h-10 w-10 rounded-full flex items-center justify-center border-2 z-10 ${
                    done
                      ? "bg-[#00E575] text-[#09090B] border-[#00E575] shadow-[0_0_14px_rgba(0,229,117,0.35)]"
                      : isCurrent
                      ? "bg-[#0E0E12] text-[#00E575] border-[#00E575] animate-pulse"
                      : "bg-[#0E0E12] text-zinc-600 border-zinc-800"
                  }`}
                >
                  {done ? <Check size={20} strokeWidth={3} /> : <Icon size={18} />}
                </div>

                <div
                  className={`rounded-2xl border p-4 transition ${
                    isCurrent
                      ? "bg-[#00E575]/8 border-[#00E575]/40 shadow-[0_0_18px_rgba(0,229,117,0.16)]"
                      : done
                      ? "bg-[#18181B] border-zinc-800"
                      : "bg-[#0E0E12] border-zinc-800/60"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div
                      className={`font-display font-bold text-[15px] ${
                        done ? "text-white" : isCurrent ? "text-zinc-100" : "text-zinc-600"
                      }`}
                    >
                      {s.label}
                    </div>
                    <div className="text-[11px] font-mono text-zinc-400 shrink-0">
                      {idx + 1}/{data.stages.length}
                    </div>
                  </div>

                  {s.hint && (
                    <div
                      className={`text-[12.5px] mt-1 leading-snug ${
                        done ? "text-zinc-300" : isCurrent ? "text-zinc-200" : "text-zinc-500"
                      }`}
                    >
                      {s.hint}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                    {done && s.completed_at && (
                      <div className="text-[12px] text-zinc-400 font-medium">
                        <Check size={11} className="inline mr-1 -mt-0.5 text-[#00E575]" />
                        am {fmtDate(s.completed_at)}
                      </div>
                    )}
                    {isCurrent && !done && (
                      <div className="text-[12px] text-[#00E575] font-semibold">
                        aktuell in Bearbeitung
                      </div>
                    )}
                    {(s.photos?.length || 0) > 0 && (
                      <div className="text-[11.5px] text-zinc-500 inline-flex items-center gap-1">
                        <ImageIcon size={11} /> {s.photos.length} Foto{s.photos.length !== 1 ? "s" : ""}
                      </div>
                    )}
                  </div>

                  {/* Foto-Galerie */}
                  {s.photos?.length > 0 && (
                    <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid={`stage-photos-${s.key}`}>
                      {s.photos.map((ph, pIdx) => (
                        <button
                          key={ph.id}
                          type="button"
                          onClick={() => setLightbox({ stageIdx: idx, photoIdx: pIdx })}
                          className="group relative aspect-square overflow-hidden rounded-lg border border-zinc-800 hover:border-[#00E575]/60 focus:outline-none focus:ring-2 focus:ring-[#00E575]/60"
                          data-testid={`stage-photo-${s.key}-${pIdx}`}
                          aria-label={ph.caption || `Foto ${pIdx + 1}`}
                        >
                          <img
                            src={photoUrl(ph.id)}
                            alt={ph.caption || ""}
                            loading="lazy"
                            className="w-full h-full object-cover transition group-hover:scale-[1.04]"
                          />
                          {ph.caption && (
                            <div className="absolute inset-x-0 bottom-0 text-[10px] px-1 py-0.5 bg-[#09090B]/80 text-zinc-200 truncate">
                              {ph.caption}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>

        <div className="mt-10 text-center text-[12.5px] text-zinc-300 leading-relaxed font-medium">
          <MapPin size={12} className="inline mr-1 -mt-0.5" />
          Fragen zu Ihrem Projekt? Ihr persönlicher NOVATUS Energie Berater meldet sich bei jedem Statuswechsel automatisch.
        </div>

        {/* Iter89r — Feedback-Panel (nur ab Commissioning) */}
        {feedback?.eligible && (
          <FeedbackPanel
            token={token}
            existing={feedback.existing}
            alreadySubmitted={feedback.already_submitted}
            onSubmitted={(entry) => setFeedback({ ...feedback, already_submitted: true, existing: entry })}
          />
        )}
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <Lightbox
          data={data}
          state={lightbox}
          onChange={setLightbox}
          onClose={() => setLightbox(null)}
          photoUrl={photoUrl}
        />
      )}
    </div>
  );
}

/**
 * Fullscreen-Lightbox mit Prev/Next-Navigation über ALLE Fotos aller Stufen.
 */
function Lightbox({ data, state, onChange, onClose, photoUrl }) {
  // Flach die Fotos aus allen Stufen
  const flat = useMemo(() => {
    const out = [];
    data.stages.forEach((st, sIdx) => {
      (st.photos || []).forEach((ph, pIdx) => {
        out.push({ ...ph, stageLabel: st.label, stageIdx: sIdx, photoIdx: pIdx });
      });
    });
    return out;
  }, [data]);

  const currentIdx = flat.findIndex(
    (f) => f.stageIdx === state.stageIdx && f.photoIdx === state.photoIdx,
  );
  const cur = flat[currentIdx];

  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && currentIdx < flat.length - 1) {
        const n = flat[currentIdx + 1];
        onChange({ stageIdx: n.stageIdx, photoIdx: n.photoIdx });
      }
      if (e.key === "ArrowLeft" && currentIdx > 0) {
        const n = flat[currentIdx - 1];
        onChange({ stageIdx: n.stageIdx, photoIdx: n.photoIdx });
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [currentIdx, flat, onChange, onClose]);

  if (!cur) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center px-4 py-6"
      data-testid="tracking-lightbox"
      onClick={onClose}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-3 right-3 h-10 w-10 rounded-full bg-zinc-900/70 border border-zinc-700 text-zinc-100 flex items-center justify-center hover:bg-zinc-800"
        aria-label="Schließen"
        data-testid="tracking-lightbox-close"
      >
        <X size={20} />
      </button>
      {currentIdx > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            const n = flat[currentIdx - 1];
            onChange({ stageIdx: n.stageIdx, photoIdx: n.photoIdx });
          }}
          className="absolute left-2 sm:left-6 h-11 w-11 rounded-full bg-zinc-900/70 border border-zinc-700 text-zinc-100 flex items-center justify-center hover:bg-zinc-800"
          aria-label="Vorheriges Foto"
        >
          <ChevronLeft size={22} />
        </button>
      )}
      {currentIdx < flat.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            const n = flat[currentIdx + 1];
            onChange({ stageIdx: n.stageIdx, photoIdx: n.photoIdx });
          }}
          className="absolute right-2 sm:right-6 h-11 w-11 rounded-full bg-zinc-900/70 border border-zinc-700 text-zinc-100 flex items-center justify-center hover:bg-zinc-800"
          aria-label="Nächstes Foto"
        >
          <ChevronRight size={22} />
        </button>
      )}
      <img
        src={photoUrl(cur.id)}
        alt={cur.caption || cur.stageLabel}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[80vh] max-w-[92vw] rounded-lg shadow-2xl object-contain"
      />
      <div className="mt-4 text-center max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="text-[13px] text-zinc-400 font-mono uppercase tracking-wider">
          {cur.stageLabel}
        </div>
        {cur.caption && (
          <div className="text-[14px] text-zinc-100 mt-1">{cur.caption}</div>
        )}
        <div className="text-[12px] text-zinc-500 mt-1">
          {currentIdx + 1} / {flat.length}
        </div>
      </div>
    </div>
  );
}


// ─── Iter89r — Feedback-Panel (Sterne + Kommentar auf Public-Track) ───────
function FeedbackPanel({ token, existing, alreadySubmitted, onSubmitted }) {
  const [rating, setRating] = useState(existing?.rating || 0);
  const [comment, setComment] = useState(existing?.comment || "");
  const [allowPublic, setAllowPublic] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(alreadySubmitted);
  const [hover, setHover] = useState(0);
  const [error, setError] = useState(null);

  const submit = async () => {
    setError(null);
    if (!rating) {
      setError("Bitte vergeben Sie eine Sternebewertung.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/public/track/${encodeURIComponent(token)}/feedback`, {
        rating, comment: comment.trim() || null, allow_public: allowPublic,
      });
      const entry = { rating, comment: comment.trim() || null, submitted_at: r.data.submitted_at };
      onSubmitted?.(entry);
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Übermittlung fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="feedback-thankyou"
        className="mt-8 bg-gradient-to-br from-[#00E575]/12 to-[#0F0F12] border-2 border-[#00E575]/40 rounded-2xl p-5 text-center"
      >
        <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#00E575]/20 border border-[#00E575]/50 mb-3">
          <Check size={22} className="text-[#00E575]" />
        </div>
        <div className="font-display font-bold text-lg text-zinc-100">Danke für Ihre Bewertung!</div>
        <div className="flex justify-center gap-0.5 mt-2">
          {[1, 2, 3, 4, 5].map((n) => (
            <Star
              key={n}
              size={20}
              className={n <= (existing?.rating || rating) ? "fill-amber-400 text-amber-400" : "text-zinc-700"}
            />
          ))}
        </div>
        {(existing?.comment || comment) && (
          <p className="mt-3 text-[12.5px] text-zinc-400 italic max-w-md mx-auto">
            „{existing?.comment || comment}"
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid="feedback-panel"
      className="mt-8 bg-gradient-to-br from-[#0F1E18] to-[#0F0F12] border border-[#00E575]/25 rounded-2xl p-5"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-[#00E575]/15 border border-[#00E575]/40 flex items-center justify-center flex-shrink-0">
          <Star size={18} className="text-[#00E575]" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-base font-bold text-zinc-100 leading-tight">
            Ihre Erfahrung mit NOVATUS
          </h3>
          <p className="text-[12.5px] text-zinc-400 mt-1 leading-snug">
            Wie zufrieden sind Sie mit der Installation? Ihre Bewertung hilft uns und anderen Kunden.
          </p>
        </div>
      </div>

      {/* Sterne */}
      <div className="flex justify-center gap-1 my-4" role="radiogroup" aria-label="Bewertung">
        {[1, 2, 3, 4, 5].map((n) => {
          const active = n <= (hover || rating);
          return (
            <button
              key={n}
              type="button"
              data-testid={`feedback-star-${n}`}
              onClick={() => setRating(n)}
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              className="p-1 transition-transform hover:scale-110"
              aria-label={`${n} von 5 Sternen`}
            >
              <Star
                size={34}
                strokeWidth={2}
                className={active ? "fill-amber-400 text-amber-400" : "text-zinc-600"}
              />
            </button>
          );
        })}
      </div>
      <p className="text-center text-[11.5px] text-zinc-500 mb-3">
        {rating === 5 ? "Ausgezeichnet — vielen Dank!"
         : rating === 4 ? "Sehr gut"
         : rating === 3 ? "Zufriedenstellend"
         : rating === 2 ? "Verbesserungswürdig"
         : rating === 1 ? "Nicht zufrieden — wir melden uns"
         : "Klicken Sie einen Stern an"}
      </p>

      {/* Kommentar */}
      <label className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold block mb-1">
        Kommentar (optional)
      </label>
      <textarea
        rows={3}
        maxLength={2000}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        data-testid="feedback-comment"
        placeholder="Wie war der Ablauf, der Kontakt, die Installation …?"
        className="w-full px-2.5 py-2 bg-[#09090B] border border-zinc-800 rounded-md text-[13px] focus:border-[#00E575] outline-none"
      />

      <label className="flex items-start gap-2 mt-3 cursor-pointer text-[12px] text-zinc-300">
        <input
          type="checkbox"
          checked={allowPublic}
          onChange={(e) => setAllowPublic(e.target.checked)}
          data-testid="feedback-allow-public"
          className="mt-0.5 accent-[#00E575]"
        />
        <span>Bewertung darf als Referenz (anonymisiert) auf der NOVATUS-Website veröffentlicht werden.</span>
      </label>

      {error && (
        <div className="mt-3 text-[12px] text-rose-300 bg-rose-500/10 border border-rose-500/40 rounded p-2">
          {error}
        </div>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={submitting || !rating}
        data-testid="feedback-submit"
        className="mt-4 w-full h-11 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13.5px] hover:bg-[#00C964] disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
      >
        {submitting
          ? <><Loader2 size={14} className="animate-spin" /> Wird gesendet …</>
          : <><Send size={14} /> Bewertung absenden</>}
      </button>
    </div>
  );
}

